import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, Sparkles, RotateCw, UserMinus, Copy, Check, X } from 'lucide-react';
import { WheelEntry } from '../../types';
import { fireWinnerConfetti } from './ConfettiEffect';

interface WinnerModalProps {
  isOpen: boolean;
  winner: WheelEntry | null;
  onClose: () => void;
  onSpinAgain: () => void;
  onRemoveAndSpin: (entryId: string) => void;
  confettiEnabled?: boolean;
}

export const WinnerModal: React.FC<WinnerModalProps> = ({
  isOpen,
  winner,
  onClose,
  onSpinAgain,
  onRemoveAndSpin,
  confettiEnabled = true,
}) => {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (isOpen && confettiEnabled) {
      fireWinnerConfetti();
    }
    setCopied(false);
  }, [isOpen, confettiEnabled]);

  // Keyboard shortcut support
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const handleCopy = () => {
    if (winner?.label) {
      navigator.clipboard.writeText(winner.label);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!winner) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0, 0, 0, 0.75)',
            backdropFilter: 'blur(12px)',
            padding: '1rem',
          }}
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.7, opacity: 0, y: 30 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.8, opacity: 0, y: 20 }}
            transition={{ type: 'spring', damping: 20, stiffness: 300 }}
            className="glass-panel"
            style={{
              position: 'relative',
              width: '100%',
              maxWidth: '480px',
              padding: 'clamp(1.5rem, 5vw, 2.5rem) clamp(1rem, 4vw, 2rem)',
              background: 'var(--bg-secondary)',
              border: '2px solid var(--border-focus)',
              borderRadius: '24px',
              textAlign: 'center',
              boxShadow: '0 25px 60px rgba(0, 0, 0, 0.6), 0 0 40px rgba(99, 102, 241, 0.4)',
              maxHeight: 'min(90vh, 90dvh)',
              overflowY: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Top Close Button */}
            <button
              onClick={onClose}
              className="btn-ghost"
              style={{
                position: 'absolute',
                top: '0.75rem',
                right: '0.75rem',
                padding: '6px',
                borderRadius: '50%',
              }}
              title="Close modal (Esc)"
            >
              <X size={20} />
            </button>

            {/* Glowing Trophy Badge */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.15, type: 'spring', stiffness: 400, damping: 15 }}
              style={{
                width: '64px',
                height: '64px',
                margin: '0 auto 1rem',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 0 30px rgba(245, 158, 11, 0.6), inset 0 2px 4px rgba(255,255,255,0.6)',
              }}
            >
              <Trophy size={34} color="#ffffff" />
            </motion.div>

            {/* Subtitle Badge */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '4px 12px',
                borderRadius: '999px',
                background: 'rgba(99, 102, 241, 0.15)',
                border: '1px solid rgba(99, 102, 241, 0.3)',
                color: 'var(--accent-primary)',
                fontSize: '0.8rem',
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                marginBottom: '0.5rem',
              }}
            >
              <Sparkles size={13} />
              <span>We Have A Winner!</span>
              <Sparkles size={13} />
            </motion.div>

            {/* Large Animated Winner Name */}
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.25, type: 'spring', stiffness: 300, damping: 18 }}
              style={{
                fontSize: 'clamp(1.6rem, 6vw, 2.5rem)',
                fontWeight: 900,
                fontFamily: 'var(--font-display)',
                letterSpacing: '-0.02em',
                lineHeight: 1.15,
                background: 'linear-gradient(135deg, #ffffff 0%, #cbd5e1 50%, #f59e0b 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                margin: '0.35rem 0 1.25rem',
                wordBreak: 'break-word',
                padding: '0 0.25rem',
              }}
            >
              {winner.label}
            </motion.div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
              <button
                onClick={() => {
                  onClose();
                  onSpinAgain();
                }}
                className="btn-primary"
                style={{
                  width: '100%',
                  padding: '0.85rem 1.5rem',
                  fontSize: '1.05rem',
                  fontWeight: 700,
                  borderRadius: '12px',
                }}
              >
                <RotateCw size={18} />
                Spin Again
              </button>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.65rem' }}>
                <button
                  onClick={() => {
                    onRemoveAndSpin(winner.id);
                    onClose();
                  }}
                  className="btn-secondary"
                  style={{
                    fontSize: '0.85rem',
                    padding: '0.65rem 0.5rem',
                    color: '#ef4444',
                  }}
                  title="Remove this winner from wheel and spin again"
                >
                  <UserMinus size={15} />
                  Remove & Spin
                </button>

                <button
                  onClick={handleCopy}
                  className="btn-secondary"
                  style={{
                    fontSize: '0.85rem',
                    padding: '0.65rem 0.5rem',
                  }}
                >
                  {copied ? (
                    <>
                      <Check size={15} color="#10b981" />
                      <span style={{ color: '#10b981' }}>Copied!</span>
                    </>
                  ) : (
                    <>
                      <Copy size={15} />
                      <span>Copy Name</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
